import { useMemo } from 'react';
import { useConfig, isYes } from '@/hooks/useConfig';
import {
  DEFAULT_LITE_MODE_SECTIONS_JSON,
  isLiteActionVisible,
  isLiteCardVisible,
  isLitePathAllowed,
  isLiteSectionEnabled,
  parseLiteModeSections,
  resolveLiteLandingPath,
  type LiteModeSectionsConfig,
} from '@/config/liteModeCatalog';

export interface LiteModeApi {
  enabled: boolean;
  sections: LiteModeSectionsConfig;
  isSectionVisible: (sectionCode: string) => boolean;
  isCardVisible: (sectionCode: string, cardCode: string) => boolean;
  isActionVisible: (sectionCode: string, actionCode: string) => boolean;
  canAccessPath: (pathname: string) => boolean;
  landingPath: string;
}

/**
 * Subtraction overlay for the full logged-in shell.
 * Callers still AND with existing flags. Guest / iCode wizards are not gated here.
 */
export function useLiteMode(): LiteModeApi {
  const enabled = isYes(useConfig('LiteModeEnabled'));
  const liteSectionsRaw = useConfig('LiteModeSections');

  return useMemo<LiteModeApi>(() => {
    const sections = parseLiteModeSections(liteSectionsRaw ?? DEFAULT_LITE_MODE_SECTIONS_JSON);
    if (!enabled) {
      return {
        enabled: false,
        sections,
        isSectionVisible: () => true,
        isCardVisible: () => true,
        isActionVisible: () => true,
        canAccessPath: () => true,
        landingPath: '/dashboard',
      };
    }
    return {
      enabled: true,
      sections,
      isSectionVisible: (sectionCode) => isLiteSectionEnabled(sections, sectionCode),
      isCardVisible: (sectionCode, cardCode) => isLiteCardVisible(sections, sectionCode, cardCode),
      isActionVisible: (sectionCode, actionCode) => isLiteActionVisible(sections, sectionCode, actionCode),
      canAccessPath: (pathname) => isLitePathAllowed(sections, pathname),
      landingPath: resolveLiteLandingPath(sections),
    };
  }, [enabled, liteSectionsRaw]);
}
